import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Form, Input, Button, Row, Col, Alert, Spin, message } from 'antd';
import {
  Elements,
  CardNumberElement,
  CardExpiryElement,
  CardCvcElement,
  useStripe,
  useElements,
} from '@stripe/react-stripe-js';
import Api from '../../helpers/apiHandler';
import { getStripe, cardBrandLabel } from '../../helpers/stripe';
// import { Lock, User } from '@ant-design/icons';

const FormItem = Form.Item;
const api = new Api();

/*
 * Card entry with Stripe Elements (Stripe Billing).
 *
 * Card number / expiry / CVV are Stripe fields: card data goes directly to Stripe and never
 * reaches our backend. Flow:
 *   1. POST cards/setup-intent -> clientSecret (+ publishableKey fallback)
 *   2. stripe.confirmCardSetup(clientSecret)  (Stripe shows 3-D Secure here if the bank asks)
 *   3. onSubmit({ ...name/address fields, paymentMethodId, setupIntentId }) -> parent calls cards/add
 *
 * The previous Fortis version posted the raw card number / CVV to cards/add; it is kept
 * commented at the end of this file.
 */

// Makes the Stripe iframes look like antd v3 inputs.
// The card number field also sets disableLink: hides Stripe Link's "Save with Link" button
// (a consumer wallet we don't use; it also overlapped the card brand label).
const ELEMENT_OPTIONS = {
  style: {
    base: {
      fontSize: '14px',
      color: 'rgba(0, 0, 0, 0.65)',
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
      '::placeholder': { color: '#bfbfbf' },
    },
    invalid: { color: '#f5222d' },
  },
};

const elementBoxStyle = {
  height: 32,
  padding: '8px 11px',
  display: 'block',
};

const emptyElementState = { complete: false, error: null, touched: false };

const StripeCardFields = (props) => {
  const {
    onSubmit,
    loading = false,
    initialValues = {},
    showBillingAddress = true,
    setup,
    requestNewSetup,
  } = props;
  const stripe = useStripe();
  const elements = useElements();
  const [confirming, setConfirming] = useState(false);
  const [cardType, setCardType] = useState('');
  const [number, setNumber] = useState(emptyElementState);
  const [expiry, setExpiry] = useState(emptyElementState);
  const [cvc, setCvc] = useState(emptyElementState);
  // A SetupIntent can only succeed once; remember used ones so a retry gets a fresh one.
  const usedSetupIds = useRef({});

  const onElementChange = (setter) => (event) => {
    setter({
      complete: event.complete,
      error: event.error ? event.error.message : null,
      touched: true,
    });
    if (event.elementType === 'cardNumber') {
      setCardType(event.brand && event.brand !== 'unknown' ? cardBrandLabel(event.brand) : '');
    }
  };

  const elementHelp = (state, emptyMessage) => {
    if (state.error) return state.error;
    if (state.touched && !state.complete) return emptyMessage;
    return undefined;
  };

  const markTouched = () => {
    setNumber((s) => ({ ...s, touched: true }));
    setExpiry((s) => ({ ...s, touched: true }));
    setCvc((s) => ({ ...s, touched: true }));
  };

  const billingDetails = (values) => {
    const address = {
      line1: values.addressLine1 || undefined,
      line2: values.addressLine2 || undefined,
      city: values.billingCity || undefined,
      state: values.billingState || undefined,
      postal_code: values.billingZipCode || undefined,
    };
    // Stripe expects a 2-letter ISO country code; the form collects free text.
    if (values.billingCountry && /^[A-Za-z]{2}$/.test(values.billingCountry.trim())) {
      address.country = values.billingCountry.trim().toUpperCase();
    }
    return { name: values.cardholderName, address };
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    markTouched();
    props.form.validateFieldsAndScroll(async (err, values) => {
      const cardComplete = number.complete && expiry.complete && cvc.complete;
      if (err || !cardComplete || !stripe || !elements) {
        return;
      }
      setConfirming(true);
      try {
        let currentSetup = setup;
        if (usedSetupIds.current[currentSetup.setupIntentId]) {
          currentSetup = await requestNewSetup();
          if (!currentSetup) return;
        }
        const result = await stripe.confirmCardSetup(currentSetup.clientSecret, {
          payment_method: {
            card: elements.getElement(CardNumberElement),
            billing_details: billingDetails(values),
          },
        });
        if (result.error) {
          message.error(result.error.message || 'Unable to verify this card. Please try again.');
          return;
        }
        usedSetupIds.current[result.setupIntent.id] = true;
        const paymentMethod = result.setupIntent.payment_method;
        onSubmit({
          ...values,
          paymentMethodId: typeof paymentMethod === 'string' ? paymentMethod : paymentMethod.id,
          setupIntentId: result.setupIntent.id,
        });
      } catch (error) {
        console.error('Stripe card setup failed:', error);
        message.error('Unable to verify this card. Please try again.');
      } finally {
        setConfirming(false);
      }
    });
  };

  return (
      <Form
        layout="vertical"
        onSubmit={handleSubmit}
      >
        <Row gutter={16}>
          <Col span={24}>
            <FormItem label="Cardholder Name">
              {props.form.getFieldDecorator('cardholderName', {
                rules: [
                  { required: true, whitespace: true, message: 'Please enter cardholder name' },
                  { min: 2, message: 'Name must be at least 2 characters' },
                  { pattern: /^[a-zA-Z\s]+$/, message: 'Name can only contain letters and spaces' }
                ],
                initialValue: initialValues.cardholderName || ''
              })(
                <Input
                  placeholder="John Doe"
                  // prefix={<User />}
                />
              )}
            </FormItem>
          </Col>
        </Row>
        <Row gutter={16}>
          <Col span={24}>
            <FormItem
              label="Card Number"
              required
              validateStatus={elementHelp(number, 'Please enter card number') ? 'error' : ''}
              help={elementHelp(number, 'Please enter card number')}
            >
              <div style={{ position: 'relative' }}>
                <div className="ant-input" style={elementBoxStyle}>
                  <CardNumberElement
                    options={{ ...ELEMENT_OPTIONS, placeholder: '1234 5678 9012 3456', disableLink: true }}
                    onChange={onElementChange(setNumber)}
                  />
                </div>
                {cardType && (
                  <span style={{ position: 'absolute', right: 11, top: 5, color: '#1890ff' }}>{cardType}</span>
                )}
              </div>
            </FormItem>
          </Col>
        </Row>

        <Row gutter={16}>
          <Col span={12}>
            <FormItem
              label="Expiry Date"
              required
              validateStatus={elementHelp(expiry, 'Please enter expiry date') ? 'error' : ''}
              help={elementHelp(expiry, 'Please enter expiry date')}
            >
              <div className="ant-input" style={elementBoxStyle}>
                <CardExpiryElement
                  options={{ ...ELEMENT_OPTIONS, placeholder: 'MM/YY' }}
                  onChange={onElementChange(setExpiry)}
                />
              </div>
            </FormItem>
          </Col>
          <Col span={12}>
            <FormItem
              label="CVV"
              required
              validateStatus={elementHelp(cvc, 'Please enter CVV') ? 'error' : ''}
              help={elementHelp(cvc, 'Please enter CVV')}
            >
              <div className="ant-input" style={elementBoxStyle}>
                <CardCvcElement
                  options={{ ...ELEMENT_OPTIONS, placeholder: '123' }}
                  onChange={onElementChange(setCvc)}
                />
              </div>
            </FormItem>
          </Col>
        </Row>

        {showBillingAddress && (
          <>
            <Row gutter={16}>
              <Col span={24}>
                <FormItem label="Street Address 1">
                  {props.form.getFieldDecorator('addressLine1', {
                    rules: [
                      { required: true, whitespace: true, message: 'Please enter street address 1' }
                    ],
                    initialValue: initialValues.addressLine1 || ''
                  })(
                    <Input
                      placeholder="123 Main Street"
                      // prefix={<Home />}
                    />
                  )}
                </FormItem>
              </Col>
            </Row>

            <Row gutter={16}>
              <Col span={24}>
                <FormItem label="Street Address 2">
                  {props.form.getFieldDecorator('addressLine2', {
                    rules: [
                      { required: false, whitespace: true, message: 'Please enter street address 2' }
                    ],
                    initialValue: initialValues.addressLine2 || ''
                  })(
                    <Input
                      placeholder="Apt 4B"
                      // prefix={<Home />}
                    />
                  )}
                </FormItem>
              </Col>
            </Row>

            <Row gutter={16}>
              <Col span={8}>
                <FormItem label="City">
                  {props.form.getFieldDecorator('billingCity', {
                    rules: [
                      { required: true, whitespace: true, message: 'Please enter city' }
                    ],
                    initialValue: initialValues.billingCity || ''
                  })(
                    <Input placeholder="New York" />
                  )}
                </FormItem>
              </Col>
              <Col span={8}>
                <FormItem label="State">
                  {props.form.getFieldDecorator('billingState', {
                    rules: [
                      { required: true, whitespace: true, message: 'Please enter state' }
                    ],
                    initialValue: initialValues.billingState || ''
                  })(
                    <Input placeholder="California" />
                  )}
                </FormItem>
              </Col>
              <Col span={8}>
                <FormItem label="Postal Code">
                  {props.form.getFieldDecorator('billingZipCode', {
                    rules: [
                      { required: true, message: 'Please enter postal code' },
                      { pattern: /^\d{5}(-\d{4})?$/, message: 'Invalid postal code format' }
                    ],
                    initialValue: initialValues.billingZipCode || ''
                  })(
                    <Input placeholder="10001" />
                  )}
                </FormItem>
              </Col>
            </Row>

            <Row gutter={16}>
              <Col span={12}>
                <FormItem label="Country">
                  {props.form.getFieldDecorator('billingCountry', {
                    rules: [
                      { required: true, whitespace: true, message: 'Please enter country' }
                    ],
                    initialValue: initialValues.billingCountry || ''
                  })(
                    <Input placeholder="United States" />
                  )}
                </FormItem>
              </Col>
            </Row>
          </>
        )}

        <FormItem style={{ marginBottom: 0 }}>
          <Button
            type="primary"
            htmlType="submit"
            loading={loading || confirming}
            disabled={!stripe}
            block
            size="large"
          >
            Add Card
          </Button>
        </FormItem>
      </Form>
  );
};

const WrappedStripeCardFields = Form.create()(StripeCardFields);

const CardDetailsForm = (props) => {
  const { userId, companyId, source, isEdit = false } = props;
  const [setup, setSetup] = useState(null);
  const [setupError, setSetupError] = useState(null);

  const requestSetupIntent = useCallback(async () => {
    setSetupError(null);
    const response = await api.post('cards/setup-intent', {
      data: { userId, companyId, ...(source ? { source } : {}) },
    });
    if (response && response.status === 1 && response.data && response.data.clientSecret) {
      setSetup(response.data);
      return response.data;
    }
    setSetupError((response && response.message) || 'Unable to start card setup. Please try again.');
    return null;
  }, [userId, companyId, source]);

  useEffect(() => {
    if (!isEdit && userId && companyId) {
      requestSetupIntent();
    }
  }, [isEdit, userId, companyId, requestSetupIntent]);

  if (isEdit) {
    // Card numbers cannot be edited with Stripe; the old edit form was a placeholder as well.
    return (
      <Alert
        type="info"
        showIcon
        message="To change card details, add a new card, set it as default, then remove this one."
      />
    );
  }

  if (!userId || !companyId) {
    return <Alert type="error" showIcon message="User or company information is not available." />;
  }

  if (setupError) {
    return (
      <Alert
        type="error"
        showIcon
        message={setupError}
        description={<Button size="small" onClick={requestSetupIntent}>Try again</Button>}
      />
    );
  }

  if (!setup) {
    return (
      <div style={{ textAlign: 'center', padding: '40px 0' }}>
        <Spin />
      </div>
    );
  }

  const stripePromise = getStripe(setup.publishableKey);
  if (!stripePromise) {
    return (
      <Alert
        type="error"
        showIcon
        message="Card payments are not configured. Please contact support."
      />
    );
  }

  return (
    <Elements stripe={stripePromise}>
      <WrappedStripeCardFields {...props} setup={setup} requestNewSetup={requestSetupIntent} />
    </Elements>
  );
};

export default CardDetailsForm;

// ===== Fortis implementation (replaced by Stripe Elements above; kept commented) =====
// import React, { useState, useEffect } from 'react';
// import { Form, Input, Button, Row, Col, Card, message, Switch } from 'antd';
// // import { Lock, User } from '@ant-design/icons';
//
// const { TextArea } = Input;
// const FormItem = Form.Item;
//
// const CardDetailsForm = (props) => {
//   const { 
//     onSubmit, 
//     loading = false, 
//     initialValues = {}, 
//     isEdit = false,
//     showBillingAddress = true 
//   } = props;
//   const [cardType, setCardType] = useState('');
//   const [cardNumber, setCardNumber] = useState('');
//   const [expiryDate, setExpiryDate] = useState('');
//
//   // Set initial values when component mounts or initialValues change
//   useEffect(() => {
//     if (initialValues.cardNumber) {
//       const formatted = formatCardNumber(initialValues.cardNumber);
//       setCardNumber(formatted);
//       const cleanValue = formatted.replace(/\s/g, '');
//       const detectedType = detectCardType(cleanValue);
//       setCardType(detectedType);
//     }
//     if (initialValues.cardExpDate) {
//       const formatted = formatExpiryDate(initialValues.cardExpDate);
//       setExpiryDate(formatted);
//     }
//   }, [initialValues]);
//
//   // Card number validation and type detection
//   const validateCardNumber = (_, value) => {
//     if (!value) {
//       return Promise.reject('Please enter card number');
//     }
//     
//     // Remove spaces and non-digits
//     const cleanValue = value.replace(/\s/g, '');
//     
//     // Luhn algorithm validation
//     if (!isValidLuhn(cleanValue)) {
//       return Promise.reject('Invalid card number');
//     }
//     
//     // Detect card type
//     const detectedType = detectCardType(cleanValue);
//     setCardType(detectedType);
//     
//     // Type-specific length validation to align with gateway rules
//     const len = cleanValue.length;
//     const allowedLengthsByType = {
//       'Visa': [13, 16, 19],
//       'Mastercard': [16],
//       'American Express': [15],
//       'Discover': [16, 17, 18, 19]
//     };
//     if (allowedLengthsByType[detectedType]) {
//       const allowed = allowedLengthsByType[detectedType];
//       if (!allowed.includes(len)) {
//         return Promise.reject(`${detectedType} must be ${allowed.join(', ')} digits`);
//       }
//     } else {
//       // Fallback generic bounds for unknown BINs
//       if (len < 13 || len > 19) {
//         return Promise.reject('Card number must be between 13-19 digits');
//       }
//     }
//     
//     return Promise.resolve();
//   };
//
//   // Luhn algorithm for card validation
//   const isValidLuhn = (cardNumber) => {
//     let sum = 0;
//     let isEven = false;
//     
//     for (let i = cardNumber.length - 1; i >= 0; i--) {
//       let digit = parseInt(cardNumber[i]);
//       
//       if (isEven) {
//         digit *= 2;
//         if (digit > 9) {
//           digit -= 9;
//         }
//       }
//       
//       sum += digit;
//       isEven = !isEven;
//     }
//     
//     return sum % 10 === 0;
//   };
//
//   // Detect card type based on number
//   const detectCardType = (cardNumber) => {
//     if (/^4/.test(cardNumber)) return 'Visa';
//     if (/^5[1-5]/.test(cardNumber)) return 'Mastercard';
//     if (/^3[47]/.test(cardNumber)) return 'American Express';
//     if (/^6/.test(cardNumber)) return 'Discover';
//     return 'Unknown';
//   };
//
//   // Format card number with spaces
//   const formatCardNumber = (value) => {
//     const v = value.replace(/\s+/g, '').replace(/[^0-9]/gi, '');
//     const matches = v.match(/\d{4,16}/g);
//     const match = matches && matches[0] || '';
//     const parts = [];
//     
//     for (let i = 0, len = match.length; i < len; i += 4) {
//       parts.push(match.substring(i, i + 4));
//     }
//     
//     if (parts.length) {
//       return parts.join(' ');
//     } else {
//       return v;
//     }
//   };
//
//   // Format expiry date
//   const formatExpiryDate = (value) => {
//     const v = value.replace(/\D/g, '');
//     if (v.length >= 2) {
//       return v.substring(0, 2) + '/' + v.substring(2, 4);
//     }
//     return v;
//   };
//
//   const handleSubmit = (e) => {
//     e.preventDefault();
//     props.form.validateFieldsAndScroll((err, values) => {
//       if (!err) {
//         // Format card number for submission
//         const formattedValues = {
//           ...values,
//           cardNumber: cardNumber.replace(/\s/g, ''),
//           cardExpDate: expiryDate.replace(/\D/g, ''),
//         };
//         
//         onSubmit(formattedValues);
//       }
//     });
//   };
//
//   return (
//     
//       <Form
//         layout="vertical"
//         onSubmit={handleSubmit}
//       >
//         <Row gutter={16}>
//           <Col span={24}>
//             <FormItem label="Cardholder Name">
//               {props.form.getFieldDecorator('cardholderName', {
//                 rules: [
//                   { required: true, whitespace: true, message: 'Please enter cardholder name' },
//                   { min: 2, message: 'Name must be at least 2 characters' },
//                   { pattern: /^[a-zA-Z\s]+$/, message: 'Name can only contain letters and spaces' }
//                 ],
//                 initialValue: initialValues.cardholderName || ''
//               })(
//                 <Input
//                   placeholder="John Doe"
//                   // prefix={<User />}
//                 />
//               )}
//             </FormItem>
//           </Col>
//         </Row>
//         <Row gutter={16}>
//           <Col span={24}>
//             <FormItem label="Card Number">
//               {props.form.getFieldDecorator('cardNumber', {
//                 rules: [
//                   { validator: validateCardNumber }
//                 ],
//                 initialValue: initialValues.cardNumber || ''
//               })(
//                 <Input
//                   placeholder="1234 5678 9012 3456"
//                   // prefix={<CreditCard />}
//                   maxLength={19}
//                   value={cardNumber}
//                   onChange={(e) => {
//                     const formatted = formatCardNumber(e.target.value);
//                     setCardNumber(formatted);
//                     // Detect card type
//                     const cleanValue = formatted.replace(/\s/g, '');
//                     const detectedType = detectCardType(cleanValue);
//                     setCardType(detectedType);
//                   }}
//                   suffix={cardType && <span style={{ color: '#1890ff' }}>{cardType}</span>}
//                 />
//               )}
//             </FormItem>
//           </Col>
//         </Row>
//
//         <Row gutter={16}>
//           <Col span={12}>
//             <FormItem label="Expiry Date">
//               {props.form.getFieldDecorator('cardExpDate', {
//                 rules: [
//                   { required: true, message: 'Please enter expiry date' },
//                   { pattern: /^(0[1-9]|1[0-2])\/\d{2}$/, message: 'Invalid expiry date (MM/YY)' }
//                 ],
//                 initialValue: initialValues.cardExpDate || ''
//               })(
//                 <Input
//                   placeholder="MM/YY"
//                   maxLength={5}
//                   value={expiryDate}
//                   onChange={(e) => {
//                     const formatted = formatExpiryDate(e.target.value);
//                     setExpiryDate(formatted);
//                   }}
//                 />
//               )}
//             </FormItem>
//           </Col>
//           <Col span={12}>
//             <FormItem label="CVV">
//               {props.form.getFieldDecorator('cardCvv', {
//                 rules: [
//                   { required: true, message: 'Please enter CVV' },
//                   { pattern: /^\d{3,4}$/, message: 'CVV must be 3-4 digits' }
//                 ],
//                 initialValue: initialValues.cardCvv || ''
//               })(
//                 <Input
//                   placeholder="123"
//                   maxLength={4}
//                   // prefix={<Lock />}
//                   type="password"
//                 />
//               )}
//             </FormItem>
//           </Col>
//         </Row>
//
//         {showBillingAddress && (
//           <>
//             <Row gutter={16}>
//               <Col span={24}>
//                 <FormItem label="Street Address 1">
//                   {props.form.getFieldDecorator('addressLine1', {
//                     rules: [
//                       { required: true, whitespace: true, message: 'Please enter street address 1' }
//                     ],
//                     initialValue: initialValues.addressLine1 || ''
//                   })(
//                     <Input
//                       placeholder="123 Main Street"
//                       // prefix={<Home />}
//                     />
//                   )}
//                 </FormItem>
//               </Col>
//             </Row>
//
//             <Row gutter={16}>
//               <Col span={24}>
//                 <FormItem label="Street Address 2">
//                   {props.form.getFieldDecorator('addressLine2', {
//                     rules: [
//                       { required: false, whitespace: true, message: 'Please enter street address 2' }
//                     ],
//                     initialValue: initialValues.addressLine2 || ''
//                   })(
//                     <Input
//                       placeholder="Apt 4B"
//                       // prefix={<Home />}
//                     />
//                   )}
//                 </FormItem>
//               </Col>
//             </Row>
//
//             <Row gutter={16}>
//               <Col span={8}>
//                 <FormItem label="City">
//                   {props.form.getFieldDecorator('billingCity', {
//                     rules: [
//                       { required: true, whitespace: true, message: 'Please enter city' }
//                     ],
//                     initialValue: initialValues.billingCity || ''
//                   })(
//                     <Input placeholder="New York" />
//                   )}
//                 </FormItem>
//               </Col>
//               <Col span={8}>
//                 <FormItem label="State">
//                   {props.form.getFieldDecorator('billingState', {
//                     rules: [
//                       { required: true, whitespace: true, message: 'Please enter state' }
//                     ],
//                     initialValue: initialValues.billingState || ''
//                   })(
//                     <Input placeholder="California" />
//                   )}
//                 </FormItem>
//               </Col>
//               <Col span={8}>
//                 <FormItem label="Postal Code">
//                   {props.form.getFieldDecorator('billingZipCode', {
//                     rules: [
//                       { required: true, message: 'Please enter postal code' },
//                       { pattern: /^\d{5}(-\d{4})?$/, message: 'Invalid postal code format' }
//                     ],
//                     initialValue: initialValues.billingZipCode || ''
//                   })(
//                     <Input placeholder="10001" />
//                   )}
//                 </FormItem>
//               </Col>
//             </Row>
//
//             <Row gutter={16}>
//               <Col span={12}>
//                 <FormItem label="Country">
//                   {props.form.getFieldDecorator('billingCountry', {
//                     rules: [
//                       { required: true, whitespace: true, message: 'Please enter country' }
//                     ],
//                     initialValue: initialValues.billingCountry || ''
//                   })(
//                     <Input placeholder="United States" />
//                   )}
//                 </FormItem>
//               </Col>
//               {/* <Col span={12}>
//                 <FormItem label="Set as Default">
//                   {props.form.getFieldDecorator('isDefault', {
//                     valuePropName: 'checked',
//                     initialValue: initialValues.isDefault || false
//                   })(
//                     <Switch />
//                   )}
//                 </FormItem>
//               </Col> */}
//             </Row>
//           </>
//         )}
//
//         <FormItem style={{ marginBottom: 0 }}>
//           <Button 
//             type="primary" 
//             htmlType="submit" 
//             loading={loading}
//             block
//             size="large"
//           >
//             {isEdit ? 'Update Card' : 'Add Card'}
//           </Button>
//         </FormItem>
//       </Form>
//   );
// };
//
// const WrappedCardDetailsForm = Form.create()(CardDetailsForm);
// export default WrappedCardDetailsForm;
// ===== end Fortis implementation =====
